import React, { useEffect, useState } from "react";
import "../../styles/quiz/Scenario.css";
import { useNavigate } from "react-router-dom";
import { fetchScenarioQuizzes } from "../../api/quizScenarioApi";
import { PieChart, Pie, Cell } from "recharts";

// Main component for the Scenario-based quiz.
// Displays realistic social media posts and asks users to choose the most responsible action.
const ScenarioPage = () => {
  // State variables
  const [quizzes, setQuizzes] = useState([]); // Loaded quiz data
  const [currentIndex, setCurrentIndex] = useState(0); // Current scenario index
  const [score, setScore] = useState(0); // Total user score
  const [showResult, setShowResult] = useState(false); // Result screen toggle
  const [userAnswers, setUserAnswers] = useState({}); // Stores user's selected options
  const [selectedOptionId, setSelectedOptionId] = useState(null); // Currently selected option
  const [finishWarning, setFinishWarning] = useState(""); // Warning if user tries to finish early
  const [selectedDetail, setSelectedDetail] = useState(null); // For showing full post again on result screen
  const navigate = useNavigate();

  // Load scenario quiz data on component mount
  useEffect(() => {
    fetchScenarioQuizzes().then(setQuizzes);
  }, []);

  // Handle selecting an option and move to next question
  const handleOptionSelect = (optionId) => {
    const currentQuiz = quizzes[currentIndex];
    const selected = currentQuiz.options.find(
      (opt) => opt.option_id === optionId
    );

    if (selected) {
      // Add score and store answer
      setScore((prev) => prev + selected.score);
      setUserAnswers((prev) => ({
        ...prev,
        [currentIndex]: {
          scenarioTitle: currentQuiz.title,
          post: currentQuiz.post,
          image_url: currentQuiz.image_url,
          selectedOption: selected,
          tip: currentQuiz.tip,
        },
      }));
    }

    setSelectedOptionId(optionId);

    // Auto-advance after short delay
    setTimeout(() => {
      if (currentIndex < quizzes.length - 1) {
        setCurrentIndex((prev) => prev + 1);
        setSelectedOptionId(
          userAnswers[currentIndex + 1]?.selectedOption?.option_id || null
        );
      }
    }, 200);
  };

  // Manually go to previous or next scenario
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setSelectedOptionId(
        userAnswers[currentIndex - 1]?.selectedOption?.option_id || null
      );
    }
  };
  const handleNext = () => {
    if (currentIndex < quizzes.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedOptionId(
        userAnswers[currentIndex + 1]?.selectedOption?.option_id || null
      );
    }
  };

  // Finalize quiz and show results if all questions are answered
  const handleFinish = () => {
    if (Object.keys(userAnswers).length === quizzes.length) {
      setShowResult(true);
      setFinishWarning("");
    } else {
      setFinishWarning(
        "Please answer all scenarios before finishing the quiz."
      );
    }
  };

  // Prepare data for pie chart
  const pieData = [
    { name: "Score", value: score },
    { name: "Remaining", value: quizzes.length * 5 - score },
  ];
  const COLORS = ["#fbc640", "#5f6f56"];
  const percent = (score / (quizzes.length * 5)) * 100;

  // Reset the quiz
  const resetQuiz = () => {
    setCurrentIndex(0);
    setScore(0);
    setShowResult(false);
    setUserAnswers({});
    setSelectedOptionId(null);
    setFinishWarning("");
    setSelectedDetail(null);
  };

  // Feedback based on total score
  const getScenarioFeedback = (score) => {
    if (score >= 22) {
      return {
        tier: "🎯 Decision Expert",
        message:
          "Incredible! You make thoughtful and responsible choices online.",
      };
    } else if (score >= 16) {
      return {
        tier: "👍 Responsible Navigator",
        message:
          "Great work! You usually think carefully before you act online.",
      };
    } else if (score >= 8) {
      return {
        tier: "🤔 Growing Citizen",
        message:
          "You're getting better! Keep practicing critical decision-making.",
      };
    } else {
      return {
        tier: "⚠️ At Risk",
        message:
          "Be careful! It's important to think critically before responding online. Try reviewing the tips.",
      };
    }
  };

  // Show loading screen while fetching data
  if (quizzes.length === 0) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading scenario quiz...</p>
      </div>
    );
  }

  const quiz = quizzes[currentIndex];

  return (
    <div className="quiz-wrapper text-center">
      {!showResult ? (
        <>
          {/* Quiz in progress */}
          <div className="scenario-layout">
            <h1 className="quiz-title mb-2">
              What would you do if you came across this post?
            </h1>
            <p className="quiz-subtitle mb-5">
              🔎 Information can be deeply misleading—always verify emotional or
              surprising claims using trusted and reliable sources.
            </p>

            {/* Thumbnail navigation for each scenario */}
            <div className="thumbnail-navigation-wrapper">
              <div className="thumbnail-navigation">
                {quizzes.map((q, idx) => (
                  <div
                    key={idx}
                    className={`thumbnail-nav-item ${
                      idx === currentIndex ? "active" : ""
                    } ${userAnswers[idx] ? "selected-answer" : ""}`}
                    onClick={() => {
                      setCurrentIndex(idx);
                      setSelectedOptionId(
                        userAnswers[idx]?.selectedOption?.option_id || null
                      );
                    }}
                  >
                    <div className="quiz-thumbnail-number">#{idx + 1}</div>
                    <img
                      src={q.image_url}
                      alt={q.title}
                      className="thumbnail-img-small"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Scenario content area */}
            <div className="scenario-content">
              {/* Progress bar */}
              <div className="progress-bar-bg mb-4">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${((currentIndex + 1) / quizzes.length) * 100}%`,
                  }}
                />
              </div>

              {/* Social media-style scenario post */}
              <div className="quiz-article-navigation">
                <button
                  className="arrow-button"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                >
                  ◀
                </button>

                <div className="post-card">
                  <div className="post-header">
                    <img
                      src="https://api.dicebear.com/7.x/initials/svg?seed=A"
                      alt="anonymous avatar"
                      className="avatar"
                    />
                    <div className="post-info">
                      <div className="username">Anonymous</div>
                      <div className="timestamp">2h · Social Media</div>
                    </div>
                  </div>

                  <div className="post-title">{quiz.title}</div>
                  <p className="post-body">{quiz.post}</p>

                  {quiz.image_url && (
                    <img
                      src={quiz.image_url}
                      alt="scenario"
                      className="post-image"
                    />
                  )}

                  <div className="reaction-bar">
                    <span>👍 1.2k</span>
                    <span>💬 243</span>
                    <span>🔁 87</span>
                  </div>
                </div>

                <button
                  className="arrow-button"
                  onClick={handleNext}
                  disabled={currentIndex >= quizzes.length - 1}
                >
                  ▶
                </button>
              </div>

              {/* Option buttons */}
              <div className="scenario-options mt-4">
                {quiz.options.map((opt) => (
                  <button
                    key={opt.option_id}
                    className={`option-button ${
                      selectedOptionId === opt.option_id
                        ? "option-selected"
                        : ""
                    }`}
                    onClick={() => handleOptionSelect(opt.option_id)}
                  >
                    {opt.option_text}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Submit and back buttons */}
          <div className="mt-5 button-row">
            <button
              className="btn btn-warning"
              onClick={() => navigate("/challenge")}
            >
              Back to Challenges
            </button>
            <button className="btn btn-warning" onClick={handleFinish}>
              Submit Quiz
            </button>
          </div>

          {/* Warning if not all scenarios are answered */}
          {finishWarning && (
            <p className="warning-text mt-2">{finishWarning}</p>
          )}
        </>
      ) : (
        <>
          {/* Result screen */}
          <h4 className="quiz-performance-heading">
            SCENARIO QUIZ PERFORMANCE
          </h4>

          <div className="performance-summary-box">
            <div className="performance-text">
              {(() => {
                const { tier, message } = getScenarioFeedback(score);
                return (
                  <>
                    <h5>{tier}</h5>
                    <p>{message}</p>
                  </>
                );
              })()}
              <p>
                Score {score}/{quizzes.length * 5}
              </p>
              {/* <p>Digital Citizenship Points: +{score * 5}</p> */}
            </div>

            {/* Score pie chart */}
            <div className="pie-chart-container">
              <PieChart width={160} height={160}>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
              <div className="pie-center-label">{Math.round(percent)}%</div>
            </div>
          </div>

          {/* Summary of answers with tips */}
          <div className="answer-bar-list">
            {Object.values(userAnswers).map((ans, i) => (
              <React.Fragment key={i}>
                <div
                  className="answer-bar-item"
                  onClick={() =>
                    setSelectedDetail(selectedDetail === i ? null : i)
                  }
                >
                  <div className="thumbnail-wrapper">
                    <img
                      src={ans.image_url}
                      alt="scenario"
                      className="thumbnail-img-small"
                    />
                  </div>

                  <div className="bar-item-text">
                    <div className="scenario-title-text">
                      {ans.scenarioTitle}
                    </div>
                    <p>
                      Your Choice: {ans.selectedOption?.option_text} —{" "}
                      <span
                        className={`risk-level-text ${ans.selectedOption?.risk_level
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {ans.selectedOption?.risk_level}
                      </span>
                    </p>
                    <p
                      className={`scenario-feedback-text-bar risk-level-text ${ans.selectedOption?.risk_level
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      📝 {ans.selectedOption?.feedback}
                    </p>
                    {ans.tip && <p className="tip-text-bar">📚 {ans.tip}</p>}
                  </div>
                </div>

                {/* Full post view on result screen */}
                {selectedDetail === i && (
                  <div className="post-card mt-3">
                    <div className="post-header">
                      <img
                        src="https://api.dicebear.com/7.x/initials/svg?seed=A"
                        alt="anonymous avatar"
                        className="avatar"
                      />
                      <div className="post-info">
                        <div className="username">Anonymous</div>
                        <div className="timestamp">2h · Social Media</div>
                      </div>
                    </div>
                    <div className="post-title">{ans.scenarioTitle}</div>
                    <p className="post-body">{ans.post}</p>
                    {ans.image_url && (
                      <img
                        src={ans.image_url}
                        alt="scenario"
                        className="post-image"
                      />
                    )}
                    <div className="reaction-bar">
                      <span>👍 1.2k</span>
                      <span>💬 243</span>
                      <span>🔁 87</span>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Retry and back buttons */}
          <div className="mt-4">
            <button className="btn btn-warning me-3" onClick={resetQuiz}>
              TRY AGAIN
            </button>
            <button
              className="btn btn-warning"
              onClick={() => navigate("/challenge")}
            >
              BACK TO CHALLENGES
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ScenarioPage;
